<script setup lang="ts">
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { useRouteParams } from "@vueuse/router";
import { CategoryType } from "mws-ts-v2-sdk";

import { queries } from "~/queries";

definePageMeta({
  ssr: true,
  middleware: "cache-control-server",
});

const isInAppBrowser = computed(() => getIsInAppBrowser());

const { result: shouldShowOneTap, loaded: growthbookLoaded } = useGrowthbook({
  feature: "account-creation-conversion-category-page-2",
  fallback: false,
  enabled: () => !isInAppBrowser.value,
});

const categorySlug = useRouteParams<string>("categorySlug");

const { data: story, suspense: storySuspense } = useStoryblokQueryWithBridge({
  folderPath: "pages/category",
  slugParamName: "categorySlug",
});

const categoryDescription = computed(
  () => story.value?.content?.categoryDescription,
);

const metaTags = computed(() => story.value?.content?.metaTags || undefined);

const { data: category, suspense: categoryQuerySuspense } = useQuery({
  ...queries.categories.details(categorySlug),
  ...notFoundQueryOptions,
});

const { data: relatedCategories, suspense: suspenseRelatedCategories } =
  useQuery({
    ...queries.categories.relatedCategories(categorySlug),
    staleTime: Infinity,
  });

const queryClient = useQueryClient();

onServerPrefetch(async () => {
  await Promise.all([
    categoryQuerySuspense(),
    storySuspense(),
    suspenseRelatedCategories(),
  ]);
  // we invalidate the query here to fetch if the user has this as a favourite
  queryClient.invalidateQueries({
    queryKey: queries.categories.details._def,
  });
});

const platformStore = usePlatformStore();
const isAuctionsEnabled =
  platformStore.getPlatformConfiguration.isAuctionsEnabled;

useCategoryUpdates(category);

const { categoryViewed } = useAnalytics();
useTrackEventOnce(category, categoryViewed);

const teamId = computed(() => {
  if (category.value?.externalReference?.type !== CategoryType.Team) return;

  return category.value.id;
});

const { setTeamName } = useTeamContext();
watchEffect(() => {
  if (teamId.value && category.value) {
    setTeamName(category.value.name);
  } else {
    setTeamName(null);
  }
});

const userAccountCreationPopupLoaded = useDelayClientLoad(2000);

// Defensive guard to prevent rendering popup during navigation/unmounting
const shouldShowPopup = computed(() => {
  return Boolean(
    teamId.value &&
      userAccountCreationPopupLoaded.value &&
      (isInAppBrowser.value ||
        (!shouldShowOneTap.value && growthbookLoaded.value)) &&
      category.value, // Ensure category data is still available
  );
});
</script>

<template>
  <div class="page-container">
    <CategoryPageHeadTags
      v-if="category"
      :category="category"
      :meta-tags="metaTags"
      :related-categories="relatedCategories"
    />
    <CategoryHeader
      :hide-banner="!isAuctionsEnabled"
      :category="category"
    />
    <template
      v-if="
        story?.content?.centerContent &&
        isStoryblokContentForWeb(story.content.showOn)
      "
    >
      <Blok
        v-for="blok in story.content?.centerContent"
        :key="blok._uid"
        v-editable="blok"
        :blok="blok"
        class="site-viewport-container"
      />
    </template>

    <!-- MWS CONTENT -->
    <template v-if="isAuctionsEnabled">
      <LazyCategoryEvents
        v-if="category"
        :category="category"
        :description="categoryDescription"
        :related-categories="relatedCategories"
      >
        <template
          v-if="
            story?.content?.bottomContent &&
            isStoryblokContentForWeb(story.content.showOn)
          "
        >
          <Blok
            v-for="blok in story.content?.bottomContent"
            :key="blok._uid"
            v-editable="blok"
            :blok="blok"
            class="site-viewport-container"
          />
        </template>
      </LazyCategoryEvents>
    </template>

    <!-- FTG CONTENT -->
    <template v-else>
      <LazyProductsOverviewBlock
        v-if="category"
        :category-identifier="categorySlug"
      />

      <template v-if="story?.content?.bottomContent">
        <Blok
          v-for="blok in story.content.bottomContent"
          :key="blok._uid"
          v-editable="blok"
          :blok="blok"
          class="site-viewport-container"
        />
      </template>
    </template>

    <ProductSectionRecentlyViewedProducts />
    <LazyUserAccountCreationPopup
      v-if="shouldShowPopup"
      :team-id="teamId"
      page-type="CATEGORY"
    />
  </div>
</template>

<style lang="postcss">
.category-block > .block-viewport-container {
  @apply site-viewport-container;
}
</style>
